import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { throttle } from 'lodash';
import { Dialog } from 'primereact/dialog';
import './PreviewModal.scss';

import { usePreviewModal } from './usePreviewModal';
import ViewerRouter, { ViewerFallback } from './ViewerRouter';
import ThumbnailStrip from './ThumbnailStrip';
import { getFileTypeV2 } from './fileTypeUtils';
import FileDownload from '../../view/list/templates/ActionsTemplate/FileDownload';
import PrintFile from '../../view/list/templates/ActionsTemplate/PrintFile';
import GetSignature from '../../view/list/templates/ActionsTemplate/GetSignature';
import { presignedURLGet } from 'modules/FileManagerV2/api/services';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PDF_MIME = 'application/pdf';
const PRINT_TYPES = new Set(['image', 'pdf']);

/**
 * Contextual action buttons rendered in the header.
 * Adapts which buttons are shown based on the current file type.
 */
const ViewerActions = React.memo(({ item, isClientFileManager, fileType }) => {
  if (!item) return null;

  const sigCondition = !isClientFileManager && item.file_type && (item.file_type === PDF_MIME || item.file_type === DOCX_MIME);

  return (
    <div className="preview-header__actions">
      {sigCondition && <GetSignature rowData={item} />}
      {PRINT_TYPES.has(fileType) && (
        <PrintFile rowData={item} isFromFilePreviewModal filePreviewModalIconClassName="preview-header__action-btn icon-Print" />
      )}
      <FileDownload rowData={item} isFromFilePreviewModal filePreviewModalIconClassName="preview-header__action-btn icon-save" />
    </div>
  );
});

ViewerActions.displayName = 'ViewerActions';

function PreviewModal() {
  const {
    showPreview,
    selectedPreviewItem,
    setSelectedPreviewItem,
    previewList,
    versionFileUpload,
    isClientFileManager,
    createActivityLog,
    handleSlideChange,
    closePreviewModal,
  } = usePreviewModal();

  const filesData = useMemo(() => {
    const base = versionFileUpload ? previewList : previewList.filter((item) => item.entry_type === 'file');

    // Guard: if the selected item isn't in the list yet (e.g. direct navigation),
    // prepend it so the viewer has something to show. We read selectedPreviewItem
    // only once here to build the initial list — changes to the selection after
    // that are handled via currentIndex, not by rebuilding filesData.
    if (!versionFileUpload && selectedPreviewItem) {
      const alreadyIn = base.some((f) => f.object_id === selectedPreviewItem.object_id);
      if (!alreadyIn) return [selectedPreviewItem, ...base];
    }
    return base;
    // Intentionally exclude selectedPreviewItem from deps — it is only needed
    // to guard against an item missing from previewList on first open. Keeping
    // it in deps causes filesData (and therefore ThumbnailStrip's items prop)
    // to get a new reference on every slide change, defeating memo on the strip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewList, versionFileUpload]);

  const selectedIndex = useMemo(() => {
    if (!selectedPreviewItem || filesData.length === 0) return 0;
    const key = versionFileUpload ? 'version_id' : 'object_id';
    const idx = filesData.findIndex((f) => f[key] === selectedPreviewItem[key]);
    return idx === -1 ? 0 : idx;
  }, [filesData, selectedPreviewItem, versionFileUpload]);

  const [currentIndex, setCurrentIndex] = useState(selectedIndex);

  useEffect(() => {
    setCurrentIndex(selectedIndex);
  }, [selectedIndex]);

  const currentItem = filesData[currentIndex] ?? null;
  const currentFileType = useMemo(() => getFileTypeV2(currentItem?.display_name || currentItem?.file_name), [currentItem]);
  const total = filesData.length;

  // Lazily-resolved presigned URLs — keyed by object_id, stored as plain strings.
  // On each file change: HEAD check the existing URL first; if it fails (or there
  // is no URL yet), call presignedURLGet to get a fresh one.
  const [resolvedUrls, setResolvedUrls] = useState({});
  const fetchingRef = useRef(new Set());
  // Tracks which objectId is currently being verified (HEAD check or presign fetch).
  // While active, a spinner is shown instead of the viewer to avoid a flash of error.
  const [verifyingObjectId, setVerifyingObjectId] = useState(null);

  useEffect(() => {
    const filePath = currentItem?.file_path;
    const objectId = currentItem?.object_id;

    if (!filePath || !objectId) return;
    if (fetchingRef.current.has(objectId)) return;

    const existingUrl = resolvedUrls[objectId] || currentItem.url;

    const done = () => {
      fetchingRef.current.delete(objectId);
      setVerifyingObjectId((prev) => (prev === objectId ? null : prev));
    };

    const fetchFreshUrl = () =>
      presignedURLGet(filePath, currentItem?.version_id, false, true, currentItem?.sk, currentItem?.pk)
        .then((res) => {
          const url = res?.data?.file_url;
          if (url) setResolvedUrls((prev) => ({ ...prev, [objectId]: url }));
        })
        .catch((err) => console.error('Failed to resolve presigned URL:', err))
        .finally(done);

    fetchingRef.current.add(objectId);
    setVerifyingObjectId(objectId);

    if (!existingUrl) {
      fetchFreshUrl();
      return;
    }

    // HEAD check — verify the URL is still accessible before rendering
    fetch(existingUrl, { method: 'HEAD' })
      .then((res) => {
        if (res.ok) {
          // Always write to resolvedUrls so it becomes the canonical source
          // and overrides any stale currentItem.url on next render
          setResolvedUrls((prev) => ({ ...prev, [objectId]: existingUrl }));
          done();
        } else {
          fetchFreshUrl();
        }
      })
      .catch(() => fetchFreshUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.object_id]);

  // Merge the resolved URL into currentItem before passing to ViewerRouter.
  // resolvedUrls takes priority over currentItem.url so a freshly-fetched URL
  // always overrides a stale one that may have been embedded in the item.
  const currentItemWithUrl = useMemo(() => {
    if (!currentItem) return null;
    const url = resolvedUrls[currentItem.object_id] || currentItem.url || null;
    return url === currentItem.url ? currentItem : { ...currentItem, url };
  }, [currentItem, resolvedUrls]);

  const isVerifyingUrl = verifyingObjectId === currentItem?.object_id;

  const handleClose = useCallback(() => {
    setResolvedUrls({});
    fetchingRef.current.clear();
    closePreviewModal();
  }, [closePreviewModal]);

  // Keep a ref to the latest filesData so goTo/goPrev/goNext never need to
  // close over filesData directly. This keeps their references stable across
  // renders, preventing ThumbnailStrip's onSelect prop from changing every time
  // the file list or current index updates.
  const filesDataRef = useRef(filesData);
  useEffect(() => {
    filesDataRef.current = filesData;
  }, [filesData]);

  const currentIndexRef = useRef(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const goTo = useCallback(
    (index) => {
      const list = filesDataRef.current;
      if (index < 0 || index >= list.length) return;
      setCurrentIndex(index);
      setSelectedPreviewItem(list[index]);
      handleSlideChange(list[index]);
    },
    [setSelectedPreviewItem, handleSlideChange]
  );

  const goPrev = useCallback(() => goTo(currentIndexRef.current - 1), [goTo]);
  const goNext = useCallback(() => goTo(currentIndexRef.current + 1), [goTo]);

  // Throttled nav handler — stable ref so the throttle state persists across renders.
  // leading:true fires on the first keydown immediately; trailing:false prevents a
  // ghost navigation after the user releases the held key.
  const throttledNavRef = useRef(null);
  useEffect(() => {
    throttledNavRef.current = throttle(
      (direction) => {
        if (direction === 'prev') goPrev();
        else goNext();
      },
      150,
      { leading: true, trailing: false }
    );
    return () => throttledNavRef.current?.cancel();
  }, [goPrev, goNext]);

  // Keyboard navigation
  useEffect(() => {
    if (!showPreview) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        throttledNavRef.current?.('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        throttledNavRef.current?.('next');
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPreview, handleClose]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < filesData.length - 1;

  // Header rendered via PrimeReact's header prop
  const headerTemplate = useCallback(
    (options) => (
      <div className="preview-header">
        <button
          type="button"
          className="preview-header__close"
          onClick={options.onHide}
          aria-label="Close preview (Esc)"
          title="Close (Esc)"
        >
          <i className="icon-window-filled modal-close-icon F-size14" />
        </button>

        <div className="preview-header__center">
          <span className="preview-header__title" title={currentItem?.display_name}>
            {currentItem?.display_name || currentItem?.file_name || ''}
          </span>
        </div>

        <ViewerActions item={currentItem} isClientFileManager={isClientFileManager} fileType={currentFileType} />
      </div>
    ),
    [currentItem, isClientFileManager, currentFileType]
  );

  return (
    <Dialog
      visible={showPreview}
      modal
      resizable={false}
      draggable={false}
      closable={false}
      className="preview-modal-v2"
      maskClassName="preview-modal-v2-mask"
      header={headerTemplate}
      onHide={handleClose}
      style={{ width: '100vw', maxWidth: '100vw', margin: 0, borderRadius: 0 }}
      contentStyle={{ padding: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {/* Screen-reader live region — announces every slide change */}
      <span className="pmv2-sr-only" aria-live="polite" aria-atomic="true">
        {currentItem ? `${currentItem.display_name || currentItem.file_name}, file ${currentIndex + 1} of ${total}` : ''}
      </span>

      <div className="preview-modal-v2__viewer-area">
        {/* Nav arrows float over the viewer */}
        <button
          type="button"
          className={`pmv2-nav pmv2-nav--prev${!hasPrev ? ' pmv2-nav--edge' : ''}`}
          onClick={goPrev}
          disabled={!hasPrev}
          aria-label="Previous file (←)"
        >
          <i className="pi pi-chevron-left" />
        </button>

        <div className="preview-modal-v2__content">
          {isVerifyingUrl ? (
            <ViewerFallback />
          ) : (
            currentItemWithUrl && (
              <ViewerRouter
                key={versionFileUpload ? currentItemWithUrl.version_id : currentItemWithUrl.object_id}
                item={currentItemWithUrl}
                isClientFileManager={isClientFileManager}
                onLoad={() => createActivityLog(currentItemWithUrl)}
              />
            )
          )}
        </div>

        <button
          type="button"
          className={`pmv2-nav pmv2-nav--next${!hasNext ? ' pmv2-nav--edge' : ''}`}
          onClick={goNext}
          disabled={!hasNext}
          aria-label="Next file (→)"
        >
          <i className="pi pi-chevron-right" />
        </button>
      </div>

      <ThumbnailStrip items={filesData} selectedIndex={currentIndex} onSelect={goTo} versionFileUpload={versionFileUpload} />
    </Dialog>
  );
}

export default PreviewModal;
